import { Button, Stack, Tooltip, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import ModalV2 from '/@/components/bayer/modal-v2';
import { type Invitation, useGetUserNode, useListInvitationsByUser } from '/@/hooks/nodes';
import { _ } from '/@/locale';
import { formatDate } from '/@/utils';

/** A FieldView authorization request can be re-sent this many days after the last one. */
const RESEND_COOLDOWN_DAYS = 7;

/**
 * "Solicitar autorización" for a grower field. Re-sends a FieldView authorization
 * invitation. The cooldown is driven purely by the most recent invitation's date,
 * NOT by connection status: a grower can already be "connected" from an old
 * accepted invitation while their token has since expired — which is exactly why
 * this error modal is showing — so we must still allow (and rate-limit) re-sends.
 * While inside the cooldown window the button is disabled and a tooltip shows the
 * date it becomes available again.
 *
 * Lives in its own component so the invitations query only runs while the error
 * modal is open (grower path) — not whenever the report modal is merely mounted.
 */
const RequestAuthorizationButton = ({
    userNodeId,
    requestAuthMutating,
    onRequestAuth,
}: {
    userNodeId: number;
    requestAuthMutating: boolean;
    onRequestAuth: () => void;
}) => {
    const userNode = useGetUserNode(userNodeId);
    const { data: invitations } = useListInvitationsByUser({ email: userNode.data?.email ?? '' });

    const lastInvitation = useMemo<Invitation | undefined>(() => {
        if (!invitations?.length) return undefined;
        return invitations.reduce((latest, inv) => (dayjs(inv.created).isAfter(dayjs(latest.created)) ? inv : latest));
    }, [invitations]);

    const withinCooldown = Boolean(
        lastInvitation && dayjs().diff(dayjs(lastInvitation.created), 'day') < RESEND_COOLDOWN_DAYS
    );
    const disabled = requestAuthMutating || withinCooldown;

    const button = (
        <Button variant='text' color='secondary' disabled={disabled} onClick={onRequestAuth}>
            {_('Solicitar autorización')}
        </Button>
    );
    if (!disabled || !lastInvitation) return button;

    const resendDate = formatDate(dayjs(lastInvitation.created).add(RESEND_COOLDOWN_DAYS, 'day').toDate());
    // Disabled buttons don't emit hover events, so the tooltip needs a wrapper.
    return (
        <Tooltip title={_('Podés reenviarla a partir del {{date}}', { date: resendDate })}>
            <span>{button}</span>
        </Tooltip>
    );
};

/**
 * Multi-state recovery modal shown when FieldView data can't be brought in.
 *
 * - `error` variant → tokens expired: grower can request authorization, own
 *   field (Bayer) can re-sync. Either way the report can be loaded manually.
 * - `no-data` variant → linkage OK but no harvest data yet: cancel or load
 *   manually.
 * - After requesting authorization the "Solicitud enviada" screen is shown;
 *   "Entendido" returns to the error modal with the request button disabled.
 */
const FieldviewReportsErrorModal = ({
    open,
    variant,
    isOwnField,
    showRequestAuth,
    showInvitationScreen,
    userNodeId,
    requestAuthMutating,
    onClose,
    onLoadManually,
    onResync,
    onRequestAuth,
    onInvitationAck,
}: {
    open: boolean;
    variant: 'error' | 'no-data';
    isOwnField: boolean;
    showRequestAuth: boolean;
    showInvitationScreen: boolean;
    userNodeId: number;
    requestAuthMutating: boolean;
    onClose: () => void;
    onLoadManually: () => void;
    onResync: () => void;
    onRequestAuth: () => void;
    onInvitationAck: () => void;
}) => {
    if (showInvitationScreen) {
        return (
            <ModalV2
                open={open}
                onClose={onClose}
                title={_('Solicitud enviada')}
                actions={
                    <Button variant='contained' color='secondary' onClick={onInvitationAck}>
                        {_('Entendido')}
                    </Button>
                }
                actionsProps={{ sx: { justifyContent: 'end' } }}
                maxWidth='sm'
            >
                <Stack spacing={2} alignItems='center' textAlign='center'>
                    <Typography color='primary' variant='subtitle1' fontWeight='bold'>
                        {_('Enviamos un mail para que el productor pueda darte permisos para conectarte con FieldView')}
                    </Typography>
                    <Typography color='primary.mui.inactive' variant='body1'>
                        {_('Podés seguir el estado de la solicitud desde la pantalla de productores.')}
                    </Typography>
                </Stack>
            </ModalV2>
        );
    }

    const isError = variant === 'error';
    const title = isError
        ? _('No pudimos traer los datos de FieldView')
        : _('No encontramos datos de cosecha en FieldView');
    const body = isError
        ? _(
              'La vinculación con Fieldview caducó. Pedile que autorice el acceso de nuevo, o cargá los datos de cosecha manualmente.'
          )
        : _(
              'Es posible que el productor todavía no los haya cargado ahi. Mientras tanto, cargá los datos manualmente.'
          );

    const secondaryAction = (() => {
        if (!isError) {
            return (
                <Button variant='text' color='secondary' onClick={onClose}>
                    {_('Cancelar')}
                </Button>
            );
        }
        if (isOwnField) {
            return (
                <Button variant='text' color='secondary' onClick={onResync}>
                    {_('Volver a sincronizar')}
                </Button>
            );
        }
        if (showRequestAuth) {
            return (
                <RequestAuthorizationButton
                    userNodeId={userNodeId}
                    requestAuthMutating={requestAuthMutating}
                    onRequestAuth={onRequestAuth}
                />
            );
        }
        return null;
    })();

    return (
        <ModalV2
            open={open}
            onClose={onClose}
            title={title}
            actions={
                <>
                    {secondaryAction}
                    <Button variant='contained' color='secondary' onClick={onLoadManually}>
                        {_('Cargar datos manualmente')}
                    </Button>
                </>
            }
            actionsProps={{ sx: { justifyContent: 'end' } }}
            maxWidth='sm'
        >
            <Typography color='primary' variant='body1' textAlign='center'>
                {body}
            </Typography>
        </ModalV2>
    );
};

export default FieldviewReportsErrorModal;
